/**
 * TableKeyboardHandler
 * 
 * Handles keyboard navigation within tables.
 * Supports Tab/Shift+Tab navigation and arrow key navigation between cells.
 */

import { TableDOMUtils } from './TableDOMUtils';

export class TableKeyboardHandler {
  /**
   * Handle Tab key navigation
   * @param cell - The current cell
   * @param shiftKey - Whether Shift key is pressed (for backward navigation)
   * @param contentEditableRef - Reference to the contentEditable container
   * @returns true if navigation was handled, false otherwise
   */
  static handleTab(cell: HTMLTableCellElement, shiftKey: boolean, contentEditableRef: HTMLDivElement): boolean {
    const table = cell.closest('table');
    if (!table) return false;

    const row = cell.parentElement as HTMLTableRowElement;
    const cells = Array.from(row.children) as HTMLTableCellElement[];
    const cellIndex = cells.indexOf(cell);

    if (shiftKey) {
      // Move to previous cell
      if (cellIndex > 0) {
        cells[cellIndex - 1].focus();
        return true;
      } else {
        // Move to last cell of previous row
        const rows = Array.from(table.querySelectorAll('tr'));
        const rowIndex = rows.indexOf(row);
        if (rowIndex > 0) {
          const prevRow = rows[rowIndex - 1];
          const prevCells = Array.from(prevRow.children) as HTMLTableCellElement[];
          prevCells[prevCells.length - 1].focus();
          return true;
        }
      }
    } else {
      // Move to next cell
      if (cellIndex < cells.length - 1) {
        cells[cellIndex + 1].focus();
        return true;
      } else {
        // Move to first cell of next row, or create new row if at end
        const rows = Array.from(table.querySelectorAll('tr'));
        const rowIndex = rows.indexOf(row);
        if (rowIndex < rows.length - 1) {
          const nextRow = rows[rowIndex + 1];
          const nextCells = Array.from(nextRow.children) as HTMLTableCellElement[];
          nextCells[0].focus();
          return true;
        } else {
          // Insert new row at end
          TableDOMUtils.insertRow(table, rowIndex, 'after');
          const newRows = Array.from(table.querySelectorAll('tr'));
          const newRow = newRows[rowIndex + 1];
          const newCells = Array.from(newRow.children) as HTMLTableCellElement[];
          newCells[0].focus();

          // Save to history
          if (contentEditableRef) {
            // Trigger history save through event
            contentEditableRef.dispatchEvent(new Event('input', { bubbles: true }));
          }

          return true;
        }
      }
    }

    return false;
  }

  /**
   * Handle arrow key navigation
   * @param cell - The current cell
   * @param direction - Direction to navigate: 'up', 'down', 'left', or 'right'
   * @returns true if navigation was handled, false otherwise
   */
  static handleArrowKey(cell: HTMLTableCellElement, direction: 'up' | 'down' | 'left' | 'right'): boolean {
    const table = cell.closest('table');
    if (!table) return false;

    const { row: rowIndex, col: colIndex } = TableDOMUtils.getCellPosition(cell);
    const rows = Array.from(table.querySelectorAll('tr'));

    let targetCell: HTMLTableCellElement | null = null;

    if (direction === 'up' && rowIndex > 0) {
      const targetRow = rows[rowIndex - 1];
      targetCell = targetRow.children[colIndex] as HTMLTableCellElement;
    } else if (direction === 'down' && rowIndex < rows.length - 1) {
      const targetRow = rows[rowIndex + 1];
      targetCell = targetRow.children[colIndex] as HTMLTableCellElement;
    } else if (direction === 'left' && colIndex > 0) {
      const currentRow = rows[rowIndex];
      targetCell = currentRow.children[colIndex - 1] as HTMLTableCellElement;
    } else if (direction === 'right') {
      const currentRow = rows[rowIndex];
      if (colIndex < currentRow.children.length - 1) {
        targetCell = currentRow.children[colIndex + 1] as HTMLTableCellElement;
      }
    }

    if (targetCell) {
      targetCell.focus();
      // Place cursor at start of cell
      const range = document.createRange();
      const sel = window.getSelection();
      if (targetCell.firstChild) {
        range.setStart(targetCell.firstChild, 0);
        range.collapse(true);
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
      return true;
    }

    return false;
  }
}
